'use client';

import React, { useState } from 'react';
import { toast } from 'sonner';
import TemplatePreviewModal from './TemplatePreviewModal';

export interface Template {
  id: string;
  name: string;
  description: string;
  category: string;
  free: boolean;
  popular: boolean;
  new: boolean;
  headerBg: string;
  pageBg: string;
  btnStyle: string;
  btnTextClass: string;
  textOnHeader: string;
  accentColor: string;
  tags: string[];
}

const templates: Template[] = [
  {
    id: 'tpl-creator',
    name: 'Creator',
    description: 'Vibrant gradient header with colorful link cards. Perfect for content creators and influencers.',
    category: 'Creator',
    free: true,
    popular: true,
    new: false,
    headerBg: 'bg-gradient-brand',
    pageBg: 'bg-gradient-to-b from-violet-50 to-background',
    btnStyle: 'bg-primary/10 border border-primary/20 text-primary hover:bg-primary/20',
    btnTextClass: 'text-primary',
    textOnHeader: 'text-white',
    accentColor: '#7C3AED',
    tags: ['Gradient', 'Colorful', 'Modern'],
  },
  {
    id: 'tpl-minimal',
    name: 'Minimal',
    description: 'Clean white design with bold typography. Let your content speak for itself.',
    category: 'Minimal',
    free: true,
    popular: false,
    new: false,
    headerBg: 'bg-white border-b border-border',
    pageBg: 'bg-white',
    btnStyle: 'bg-foreground text-white border-0 hover:bg-foreground/90',
    btnTextClass: 'text-white',
    textOnHeader: 'text-foreground',
    accentColor: '#1A1035',
    tags: ['Clean', 'Minimal', 'Professional'],
  },
  {
    id: 'tpl-influencer',
    name: 'Influencer',
    description: 'Bold pink-to-rose gradient. Made for lifestyle creators and fashion influencers.',
    category: 'Creator',
    free: true,
    popular: true,
    new: false,
    headerBg: 'bg-gradient-to-r from-pink-400 to-rose-500',
    pageBg: 'bg-gradient-to-b from-pink-50 to-background',
    btnStyle: 'bg-pink-500 text-white border-0 hover:bg-pink-600',
    btnTextClass: 'text-white',
    textOnHeader: 'text-white',
    accentColor: '#EC4899',
    tags: ['Pink', 'Bold', 'Lifestyle'],
  },
  {
    id: 'tpl-business',
    name: 'Business',
    description: 'Professional emerald theme. Ideal for entrepreneurs, coaches and service businesses.',
    category: 'Business',
    free: false,
    popular: false,
    new: false,
    headerBg: 'bg-gradient-to-r from-emerald-500 to-teal-600',
    pageBg: 'bg-gradient-to-b from-emerald-50 to-background',
    btnStyle: 'bg-emerald-600 text-white border-0 hover:bg-emerald-700',
    btnTextClass: 'text-white',
    textOnHeader: 'text-white',
    accentColor: '#059669',
    tags: ['Professional', 'Green', 'Corporate'],
  },
  {
    id: 'tpl-dark',
    name: 'Dark Mode',
    description: 'Sleek dark background with glowing accents. For creators with a bold, edgy aesthetic.',
    category: 'Premium',
    free: false,
    popular: false,
    new: false,
    headerBg: 'bg-gradient-to-r from-violet-900 to-purple-900',
    pageBg: 'bg-gradient-to-b from-gray-900 to-gray-950',
    btnStyle: 'bg-white/10 border border-white/20 text-white hover:bg-white/20',
    btnTextClass: 'text-white',
    textOnHeader: 'text-white',
    accentColor: '#A855F7',
    tags: ['Dark', 'Edgy', 'Neon'],
  },
  {
    id: 'tpl-neon',
    name: 'Neon',
    description: 'Cyberpunk-inspired neon gradients on deep dark backgrounds. Stand out from the crowd.',
    category: 'Premium',
    free: false,
    popular: false,
    new: true,
    headerBg: 'bg-gradient-to-r from-cyan-500 to-blue-600',
    pageBg: 'bg-gradient-to-b from-slate-900 to-slate-950',
    btnStyle: 'bg-cyan-500/20 border border-cyan-500/40 text-cyan-400 hover:bg-cyan-500/30',
    btnTextClass: 'text-cyan-400',
    textOnHeader: 'text-white',
    accentColor: '#06B6D4',
    tags: ['Neon', 'Cyberpunk', 'Futuristic'],
  },
];

const categories = ['All', 'Creator', 'Minimal', 'Business', 'Premium'];

export default function TemplateGallery() {
  const [activeCategory, setActiveCategory] = useState('All');
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);
  const [appliedTemplate, setAppliedTemplate] = useState('tpl-creator');
  const [applyingId, setApplyingId] = useState<string | null>(null);

  const filtered = activeCategory === 'All'
    ? templates
    : templates.filter((t) => t.category === activeCategory);

  const handleApply = async (template: Template) => {
    if (!template.free) {
      toast.error('Upgrade to Pro to unlock this template');
      return;
    }
    setApplyingId(template.id);
    // BACKEND INTEGRATION: PUT /api/profile/template with { templateId: template.id }
    await new Promise((r) => setTimeout(r, 1000));
    setAppliedTemplate(template.id);
    setApplyingId(null);
    toast.success(`"${template.name}" template applied to your bio page! 🎨`);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-700 text-foreground">Templates</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Choose a design for your bio page. Applied instantly — no republishing needed.
          </p>
        </div>
        <span className="flex-shrink-0 text-xs font-600 px-3 py-1.5 rounded-full bg-accent/10 text-accent border border-accent/20">
          {appliedTemplate && templates.find((t) => t.id === appliedTemplate)?.name} Active
        </span>
      </div>

      {/* Category filter */}
      <div className="flex flex-wrap gap-2">
        {categories.map((cat) => (
          <button
            key={`cat-${cat}`}
            onClick={() => setActiveCategory(cat)}
            className={`px-4 py-2 rounded-full text-sm font-600 border transition-all duration-200 ${
              activeCategory === cat
                ? 'bg-primary text-white border-primary' :'bg-card border-border text-muted-foreground hover:border-primary/40 hover:text-foreground'
            }`}
          >
            {cat}
          </button>
        ))}
      </div>

      {/* Template grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 2xl:grid-cols-3 gap-5">
        {filtered.map((template) => (
          <div
            key={template.id}
            className={`relative bg-card rounded-2xl border overflow-hidden transition-all duration-300 hover:card-shadow-hover hover:-translate-y-1 ${
              appliedTemplate === template.id
                ? 'border-primary ring-2 ring-primary/20' :'border-border card-shadow'
            }`}
          >
            {/* Badges */}
            <div className="absolute top-3 left-3 z-10 flex gap-2">
              {template.popular && (
                <span className="text-xs font-700 px-2 py-0.5 rounded-full bg-accent text-white">
                  Popular
                </span>
              )}
              {template.new && (
                <span className="text-xs font-700 px-2 py-0.5 rounded-full bg-success text-white">
                  New
                </span>
              )}
              {!template.free && (
                <span className="text-xs font-700 px-2 py-0.5 rounded-full bg-foreground/80 text-white flex items-center gap-1">
                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                  </svg>
                  Pro
                </span>
              )}
            </div>

            {appliedTemplate === template.id && (
              <div className="absolute top-3 right-3 z-10">
                <span className="flex items-center gap-1 text-xs font-700 px-2 py-0.5 rounded-full bg-primary text-white">
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                  </svg>
                  Active
                </span>
              </div>
            )}

            {/* Preview */}
            <div className={`${template.pageBg} overflow-hidden`}>
              <div className={`${template.headerBg} p-4 flex flex-col items-center`}>
                <div className="w-10 h-10 rounded-full bg-white/30 border-2 border-white/50 flex items-center justify-center font-800 text-sm text-white mb-2">
                  PS
                </div>
                <p className={`font-700 text-sm ${template.textOnHeader}`}>Priya Sharma</p>
                <p className={`text-xs mt-0.5 ${template.textOnHeader} opacity-70`}>Content Creator</p>
              </div>

              <div className="p-3 space-y-2">
                {['Latest Video', 'Instagram', 'Shop Now'].map((label, i) => (
                  <div
                    key={`${template.id}-prev-${i}`}
                    className={`py-2 px-3 rounded-xl text-xs font-600 text-center ${template.btnStyle}`}
                  >
                    {label}
                  </div>
                ))}
              </div>
            </div>

            {/* Info */}
            <div className="p-4 border-t border-border">
              <div className="flex items-start justify-between mb-2">
                <div>
                  <h3 className="text-sm font-700 text-foreground">{template.name}</h3>
                  <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{template.description}</p>
                </div>
              </div>

              <div className="flex flex-wrap gap-1 mb-3">
                {template.tags.map((tag) => (
                  <span key={`${template.id}-tag-${tag}`} className="text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground">
                    {tag}
                  </span>
                ))}
              </div>

              <div className="flex gap-2">
                <button
                  onClick={() => setSelectedTemplate(template)}
                  className="flex-1 py-2 rounded-xl border border-border text-xs font-600 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                >
                  Preview
                </button>
                <button
                  onClick={() => handleApply(template)}
                  disabled={applyingId === template.id || appliedTemplate === template.id}
                  className={`flex-1 py-2 rounded-xl text-xs font-600 transition-all duration-200 flex items-center justify-center gap-1 ${
                    appliedTemplate === template.id
                      ? 'bg-success/10 text-success border border-success/20 cursor-default'
                      : !template.free
                      ? 'bg-foreground/5 text-muted-foreground border border-border hover:bg-muted'
                      : 'btn-primary'
                  }`}
                >
                  {applyingId === template.id ? (
                    <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                  ) : appliedTemplate === template.id ? (
                    '✓ Applied'
                  ) : !template.free ? (
                    '🔒 Upgrade'
                  ) : (
                    'Apply'
                  )}
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Upgrade banner */}
      <div className="bg-gradient-brand rounded-2xl p-6 flex flex-col sm:flex-row items-center justify-between gap-4">
        <div>
          <p className="text-white font-700 text-lg mb-1">Unlock all premium templates</p>
          <p className="text-white/70 text-sm">Get access to Dark, Neon, and future templates with a Pro plan.</p>
        </div>
        <button className="flex-shrink-0 bg-white text-primary font-700 text-sm px-6 py-3 rounded-xl hover:bg-white/90 transition-colors whitespace-nowrap">
          Upgrade to Pro — ₹399/mo
        </button>
      </div>

      {/* Preview modal */}
      {selectedTemplate && (
        <TemplatePreviewModal
          template={selectedTemplate}
          isApplied={appliedTemplate === selectedTemplate.id}
          onApply={() => handleApply(selectedTemplate)}
          onClose={() => setSelectedTemplate(null)}
        />
      )}
    </div>
  );
}